/**
 * Notification service -- push token registration + management.
 * Class-based singleton per architecture rules.
 */
import { Platform } from 'react-native'
import * as Notifications from 'expo-notifications'
import * as Device from 'expo-device'
import Constants from 'expo-constants'
import { supabase } from '../supabase'
import { addBreadcrumb } from '../utils/observability'

import { productTelemetryService } from './product-telemetry.service'
import { isIsolatedPreviewBuild } from '../runtime-build'

class NotificationServiceImpl {
  /**
   * Request notification permissions and register the push token.
   * Returns the Expo push token string, or null if denied/unavailable.
   */
  async registerPushToken(userId: string): Promise<string | null> {
    // Web doesn't support native push tokens
    // Preview has an isolated Supabase fixture backend but intentionally no
    // Firebase app. Never attempt to obtain or persist a production-capable
    // push token from that separate application identity.
    if (Platform.OS === 'web' || isIsolatedPreviewBuild) return null

    // Ensure notification channel is configured on Android
    if (Platform.OS === 'android') {
      try {
        await Notifications.setNotificationChannelAsync('default', {
          name: 'Tago',
          importance: Notifications.AndroidImportance.HIGH,
          vibrationPattern: [0, 250, 250, 250],
          lightColor: '#480C82',
        })
      } catch (err) {
        addBreadcrumb('notifications', 'Android notification channel unavailable', {
          message: err instanceof Error ? err.message : String(err),
        })
      }
    }

    let finalStatus: Notifications.PermissionStatus | undefined
    try {
      const { status: existingStatus } = await Notifications.getPermissionsAsync()
      finalStatus = existingStatus

      if (existingStatus !== 'granted') {
        const { status } = await Notifications.requestPermissionsAsync()
        finalStatus = status
      }
    } catch (err) {
      addBreadcrumb('notifications', 'Permission request unavailable', {
        message: err instanceof Error ? err.message : String(err),
      })
    }

    productTelemetryService.recordEvent('system', 'push.permission_check', {
      metadata: {
        permissionStatus: finalStatus ?? 'unknown',
        platform: Platform.OS,
        userId,
      },
    })

    // Get push token
    const projectId =
      Constants.expoConfig?.extra?.eas?.projectId ??
      Constants.easConfig?.projectId ??
      '271e57e4-b00a-4bbe-a718-b485b7880fbd'

    let token: string | null = null
    let tokenError: string | null = null

    try {
      const tokenData = await Notifications.getExpoPushTokenAsync({ projectId })
      token = tokenData.data
    } catch (err) {
      tokenError = err instanceof Error ? err.message : String(err)
      addBreadcrumb('notifications', 'Expo push token unavailable, trying device token', {
        message: tokenError,
      })

      try {
        const deviceTokenData = await Notifications.getDevicePushTokenAsync()
        token = deviceTokenData.data
      } catch (devErr) {
        const devErrMsg = devErr instanceof Error ? devErr.message : String(devErr)
        productTelemetryService.recordEvent('system', 'push.token_error', {
          metadata: {
            expoError: tokenError,
            deviceError: devErrMsg,
            userId,
          },
        })
        return null
      }
    }

    if (!token) {
      productTelemetryService.recordEvent('system', 'push.token_empty', {
        metadata: {
          expoError: tokenError,
          userId,
        },
      })
      return null
    }

    const provider =
      token.startsWith('ExponentPushToken') || token.startsWith('ExpoPushToken') ? 'expo' : 'fcm'

    // Save to database (upsert by token value)
    const { error: dbError } = await supabase.from('push_tokens').upsert(
      {
        profile_id: userId,
        token,
        provider,
        device_type: Platform.OS,
        device_name: Device.modelName ?? Device.deviceName ?? 'Unknown',
        is_active: true,
        failure_count: 0,
        last_used_at: new Date().toISOString(),
      },
      { onConflict: 'token' },
    )

    productTelemetryService.recordEvent('system', 'push.token_saved', {
      metadata: {
        success: !dbError,
        dbError: dbError?.message ?? null,
        provider,
        tokenPrefix: token.substring(0, 15),
        userId,
      },
    })

    if (dbError) {
      addBreadcrumb('notifications', 'Failed to save push token', {
        code: dbError.code,
        message: dbError.message,
      })
    }

    // Set up Android notification channel
    if (Platform.OS === 'android') {
      try {
        await Notifications.setNotificationChannelAsync('default', {
          name: 'Tago',
          importance: Notifications.AndroidImportance.HIGH,
          vibrationPattern: [0, 250, 250, 250],
          lightColor: '#480C82',
        })
      } catch (err) {
        addBreadcrumb('notifications', 'Android notification channel unavailable', {
          message: err instanceof Error ? err.message : String(err),
        })
      }
    }

    return token
  }

  /**
   * Deactivate the current device's push token (on logout).
   */
  async unregisterPushToken(userId: string): Promise<void> {
    // Web has no push tokens to unregister — same Platform guard as
    // registerPushToken to keep the Expo "not supported on web" warning
    // out of the console.
    if (Platform.OS === 'web' || isIsolatedPreviewBuild) return
    try {
      const projectId =
        Constants.expoConfig?.extra?.eas?.projectId ??
        Constants.easConfig?.projectId ??
        '271e57e4-b00a-4bbe-a718-b485b7880fbd'
      if (!projectId) return

      const { data: token } = await Notifications.getExpoPushTokenAsync({ projectId })

      await supabase
        .from('push_tokens')
        .update({ is_active: false })
        .eq('profile_id', userId)
        .eq('token', token)
    } catch {
      // Silent fail on logout -- not critical
    }
  }
}

export const notificationService = new NotificationServiceImpl()
